import React from "react";
import { IconColor } from "../../../../icons";
import DocsNiceToKnow from "../../shared/DocsNiceToKnow";
import DocsPreviewPanel from "../../shared/DocsPreviewPanel";
import DocsSectionCard from "../../shared/DocsSectionCard";

const baseGroups = [
  {
    title: "Text",
    items: [
      { token: "--ds-color-text-primary", usage: "Default-Schriftfarbe", preview: "text" },
      { token: "--ds-color-text-secondary", usage: "Subtitles und sekundäre Informationen", preview: "text" },
      { token: "--ds-color-text-tertiary", usage: "Meta-Angaben, Help Text und Placeholder", preview: "text" },
      { token: "--ds-color-text-inverse", usage: "Text auf dunklen oder brand-farbigen Flächen", preview: "text-inverse" },
    ],
  },
  {
    title: "Border",
    items: [
      { token: "--ds-color-border-default", usage: "Standard-Border für alles (Cards, Boxen, Trennlinien, etc.)", preview: "border" },
      { token: "--ds-color-border-strong", usage: "Falls nötig für stärkere Abgrenzung", preview: "border" },
      { token: "--ds-color-border-emphasis", usage: "eigentlich nicht im Einsatz, primär Hover von Kompnenten", preview: "border" },
    ],
  },
  {
    title: "Surface",
    items: [
      { token: "--ds-color-surface-white", usage: "Alles was weisser Background hat (Cards, Dialoge, etc.)", preview: "surface" },
      { token: "--ds-color-surface-default", usage: "Page-Hintergrund", preview: "surface" },
      { token: "--ds-color-surface-subtle", usage: "Bewusst leicht abgesetzte Boxen", preview: "surface" },
    ],
  },
];

const brandItems = [
  { token: "--ds-color-brand-default-light", usage: "Helle Brand-Farbe" },
  { token: "--ds-color-brand-default", usage: "PRIMÄRE BRAND-FARBE" },
  { token: "--ds-color-brand-default-strong", usage: "Hover und Pressed auf Brand-Farbe" },
  { token: "--ds-color-brand-muted", usage: "UNSER DEFAULT ROW-HOVER, helle Brand-Backgrounds" },
  { token: "--ds-color-brand-muted-strong", usage: "Default Background für Selected-Hover, etwas dünklerer Brand-Background" },
  { token: "--ds-color-brand-muted-strong2", usage: "Stärkere Akzentflächen in Brand Farbe falls nötig" },
];

const statusFamilies = [
  { key: "success", label: "Success" },
  { key: "warning", label: "Warning" },
  { key: "error", label: "Error" },
  { key: "info", label: "Info" },
];

const statusRoles = [
  { suffix: "default", label: "default", usage: "Text / Icon" },
  { suffix: "default-strong", label: "default-strong", usage: "Hover / Pressed" },
  { suffix: "muted", label: "muted", usage: "Background" },
  { suffix: "muted-strong", label: "muted-strong", usage: "Strong background" },
];

const categoryColors = [
  { key: "01", label: "Rot" },
  { key: "02", label: "Grün" },
  { key: "03", label: "Blau" },
  { key: "04", label: "Gelb" },
  { key: "05", label: "Pink" },
  { key: "06", label: "Schwarz" },
  { key: "07", label: "Orange" },
  { key: "08", label: "Violett" },
  { key: "09", label: "Türkis" },
  { key: "10", label: "Braun" },
];

const categoryRoles = [
  { key: "default", usage: "Text / Markierung / Balken" },
  { key: "muted", usage: "Chip-Hintergrund" },
];

const niceToKnowItems = [
  "border-default für nahezu alle Borders von Boxen in der Software",
  "surface-subtle für Hintergründe von Elementen die abheben sollen",
  "brand-muted für alles was Hover ist.",
  "brand-muted-strong für alles was Selected ist.",
  "Kategorienfarben immer in Reihenfolge vergeben: 01–06 zuerst verwenden, danach bei Bedarf 07–10.",
];

function ColorPreview({ token, type = "color" }) {
  return (
    <span
      className={`ds-docs-color-preview ds-docs-color-preview--${type}`}
      style={{ "--ds-docs-color-value": `var(${token})` }}
      aria-hidden="true"
    >
      {type.startsWith("text") ? "Aa" : null}
    </span>
  );
}

function ColorToken({ item }) {
  return (
    <div className="ds-docs-color-token">
      <ColorPreview token={item.token} type={item.preview} />
      <div className="ds-docs-color-token__content">
        <code className="ds-docs-color-token__name">{item.token}</code>
        <span className="ds-docs-color-token__usage">{item.usage}</span>
      </div>
    </div>
  );
}

function BaseGroup({ group }) {
  return (
    <section className="ds-docs-color-group">
      <h4 className="ds-docs-color-group__title">{group.title}</h4>
      <div className="ds-docs-color-token-list">
        {group.items.map((item) => (
          <ColorToken key={item.token} item={item} />
        ))}
      </div>
    </section>
  );
}

function StatusFamily({ family }) {
  return (
    <section className="ds-docs-color-status-family">
      <h4 className="ds-docs-color-status-family__title">{family.label}</h4>
      <div className="ds-docs-color-status-family__grid">
        {statusRoles.map((role) => {
          const token = `--ds-color-${family.key}-${role.suffix}`;

          return (
            <div key={token} className="ds-docs-color-status-token">
              <ColorPreview token={token} />
              <div className="ds-docs-color-status-token__content">
                <code>{role.label}</code>
                <span>{role.usage}</span>
              </div>
            </div>
          );
        })}
      </div>
      <code className="ds-docs-color-status-family__pattern">--ds-color-{family.key}-*</code>
    </section>
  );
}

function CategoryColor({ category }) {
  return (
    <article
      className="ds-docs-color-category"
      style={{
        "--ds-docs-category-default": `var(--ds-color-category-${category.key}-default)`,
        "--ds-docs-category-muted": `var(--ds-color-category-${category.key}-muted)`,
      }}
    >
      <div className="ds-docs-color-category__header">
        <span className="ds-docs-color-category__marker" aria-hidden="true" />
        <strong>{`${category.key} · ${category.label}`}</strong>
        <span className="ds-docs-color-category__chip">7A</span>
      </div>
      <div className="ds-docs-color-category__bar" aria-hidden="true" />
      <div className="ds-docs-color-category__roles">
        {categoryRoles.map((role) => {
          const token = `--ds-color-category-${category.key}-${role.key}`;

          return (
            <div key={token} className="ds-docs-color-category__role">
              <ColorPreview token={token} />
              <div>
                <code>{token}</code>
                <span>{role.usage}</span>
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function SectionHeading({ title, description }) {
  return (
    <div className="ds-docs-color-section__heading">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

export default function ColorsDocs() {
  return (
    <DocsSectionCard title="Colors">
      <div className="ds-docs-spotlight-grid ds-docs-spotlight-grid--colors">
        <DocsPreviewPanel label="Tokens" icon={IconColor}>
          <div className="ds-docs-preview-surface ds-docs-color-sheet">
            <section className="ds-docs-color-section">
              <SectionHeading title="Base" description="Neutrale Farben für Inhalt, Konturen und Flächen." />
              <div className="ds-docs-color-base-grid">
                {baseGroups.map((group) => (
                  <BaseGroup key={group.title} group={group} />
                ))}
              </div>
            </section>

            <section className="ds-docs-color-section">
              <SectionHeading title="Brand" description="Aktionen, Interaktion und brand-getönte Zustandsflächen." />
              <div className="ds-docs-color-brand-grid">
                {brandItems.map((item) => (
                  <ColorToken key={item.token} item={item} />
                ))}
              </div>
            </section>

            <section className="ds-docs-color-section">
              <SectionHeading title="Status" description="Alle Statusfamilien folgen derselben Rollenlogik." />
              <div className="ds-docs-color-status-grid">
                {statusFamilies.map((family) => (
                  <StatusFamily key={family.key} family={family} />
                ))}
              </div>
            </section>

            <section className="ds-docs-color-section">
              <SectionHeading
                title="Categories"
                description="Nummerierte Farben 01–10 für Klassen und andere kategoriale Daten."
              />
              <div className="ds-docs-color-category-grid">
                {categoryColors.map((category) => (
                  <CategoryColor key={category.key} category={category} />
                ))}
              </div>
            </section>
          </div>
        </DocsPreviewPanel>

        <DocsNiceToKnow
          intro="Die wichtigsten Farb-Tokens sind:"
          items={niceToKnowItems}
        />
      </div>
    </DocsSectionCard>
  );
}
